import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

// Styled replacement for <select>: the native menu ignores the site's styling.
// options: [{ value, label }]
const Dropdown = ({ value, options, onChange, ariaLabel, align = "left" }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = options.find((o) => o.value === value) || options[0];

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        className={`flex items-center gap-2 py-2 pl-4 pr-3 text-sm font-medium text-gray-900 bg-white border rounded-full transition hover:border-[#d4af37] ${
          open ? "border-[#d4af37] ring-2 ring-[#d4af37]/20" : "border-gray-300"
        }`}
      >
        {selected.label}
        <ChevronDown
          className={`w-4 h-4 text-gray-500 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          className={`absolute z-30 mt-2 min-w-full py-2 bg-white border border-gray-100 shadow-xl rounded-xl ${
            align === "right" ? "right-0" : "left-0"
          }`}
        >
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <li key={option.value} role="option" aria-selected={isSelected}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                  className={`flex items-center justify-between w-full gap-6 px-4 py-2 text-sm text-left whitespace-nowrap transition hover:bg-[#f8f6fc] ${
                    isSelected
                      ? "text-[#1a1a3e] font-semibold"
                      : "text-gray-600"
                  }`}
                >
                  {option.label}
                  <Check
                    className={`w-4 h-4 text-[#d4af37] ${
                      isSelected ? "" : "invisible"
                    }`}
                  />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default Dropdown;
